import React from "react";
import { useAppContext } from "../../../AppProvider";
import { useTypes } from "../../../utilities/types";

export const useListProps = () => {
  const { dictionary } = useAppContext();
  return {
    path: "/logistic-service/order-details",
    title: <h3>{dictionary.products.products}</h3>,
    pagination: { enabled: false, page: 0, size: 200 },
    selectRowsEnabled: { enabled: false },
    actionBar: { enabled: false },
    sorting: [
      {
        orderBy: "desc",
        column: "article.shippingRequired",
        isDefault: true,
      },
    ],
  };
};

export const ListHeader = React.memo(({ isPrint }) => {
  const { dictionary, breakpoint } = useAppContext();
  const gridTemplateColumns =
    ["xs", "sm", "md"].includes(breakpoint) && !isPrint
      ? "2fr 6fr 1fr 3fr"
      : "0.5fr 1.5fr 0.75fr 0.75fr 0.75fr 0.75fr 0.75fr 0.25fr 0.75fr";

  return (
    <div
      className="d-grid grid-column align-items-center p-1"
      style={{
        gridTemplateColumns: gridTemplateColumns,
      }}
    >
      <div></div>
      <div>
        <label className="label-2">{dictionary.products.product}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-2">{dictionary.registries.description}</label>
      </div>
      <div>
        <label className="label-2">{dictionary.dimensions.quantity}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-2">
          {dictionary.dimensions.unit_price}
          <br />({dictionary.words.vat} {dictionary.words.excluding_fs})
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-2">
          {dictionary.words.discount}
          <br />({dictionary.words.vat} {dictionary.words.excluding_fs})
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-2">
          {dictionary.words.total}
          <br />({dictionary.words.vat} {dictionary.words.excluding_fs})
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-2">{dictionary.words.vat}</label>
      </div>
      <div className="text-end">
        <label className="label-2">
          {dictionary.words.total}
          <br />({dictionary.words.vat} {dictionary.words.including_fs})
        </label>
      </div>
    </div>
  );
});

export const ListItem = React.memo(({ item, isPrint }) => {
  const { breakpoint } = useAppContext();
  const types = useTypes();

  const currency =
    types.currencies.find((x) => x.value === item?.currency)?.label || "";

  const vatRate = item.vat ? 1 + item.vat / 100 : 1.22;
  const unitPrice = item.unitPrice?.toFixed(2) || "-";
  const totalPrice = item.totalPrice?.toFixed(2) || "-";
  const totalWithVat = ((item.totalPrice || 0) * vatRate).toFixed(2);
  const discount = item.discount
    ? `${item.discount.toFixed(2)} ${currency}`
    : "";

  const Image = () =>
    item.articleImageUrl ? (
      <img
        style={{ maxWidth: "25px", maxHeight: "25px" }}
        src={item.articleImageUrl}
        alt="article"
      />
    ) : null;

  const gridTemplateColumns =
    ["xs", "sm", "md"].includes(breakpoint) && !isPrint
      ? "2fr 6fr 1fr 3fr"
      : "0.5fr 1.5fr 0.75fr 0.75fr 0.75fr 0.75fr 0.75fr 0.25fr 0.75fr";

  return (
    <div
      className={`${
        isPrint ? "border-bottom" : "border"
      } ddt-row d-grid grid-column align-items-center bg-color-3 border-light  mb-1 p-1`}
      style={{
        gridTemplateColumns: gridTemplateColumns,
      }}
    >
      <div className="text-center py-1">
        <Image />
      </div>
      <div>
        <label className="label-5">
          {item.articleName}{" "}
          <span className="d-block d-lg-none span-1">{item.barcode}</span>
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-5">{item.barcode}</label>
      </div>
      <div>
        <label className="label-5">{item.quantity}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-5">
          {unitPrice} {currency}
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-5">{discount}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-5">
          {totalPrice} {currency}
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-5">{item.vat}%</label>
      </div>
      <div className="text-end">
        <label className="label-5">
          {totalWithVat} {currency}
        </label>
      </div>
    </div>
  );
});

export const ListFooter = React.memo(({ data, isPrint }) => {
  const { dictionary, breakpoint } = useAppContext();
  const types = useTypes();

  const currency =
    types.currencies.find((x) => x.value === data?.currency)?.label || "";

  const totals = {
    quantity: data.orderDetails?.reduce((sum, x) => sum + (x.quantity || 0), 0),
    unitPrice: data.orderDetails?.reduce(
      (sum, x) => sum + (x.unitPrice || 0),
      0
    ),
    discount: data.orderDetails?.reduce((sum, x) => sum + (x.discount || 0), 0),
    total: data.orderDetails?.reduce((sum, x) => sum + (x.totalPrice || 0), 0),
    totalWithVat: data.orderDetails?.reduce(
      (sum, x) => sum + (x.totalPrice || 0) * (x.vat ? 1 + x.vat / 100 : 1.22),
      0
    ),
  };

  const gridTemplateColumns =
    ["xs", "sm", "md"].includes(breakpoint) && !isPrint
      ? "8fr 1fr 3fr"
      : "0.5fr 1.5fr 0.75fr 0.75fr 0.75fr 0.75fr 0.75fr 0.25fr 0.75fr";

  return (
    <div
      className="d-grid grid-column align-items-center mt-2 p-1"
      style={{
        gridTemplateColumns: gridTemplateColumns,
      }}
    >
      <div>
        <label className="label-3">{dictionary.words.total}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}></div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}></div>
      <div>
        <label className="label-3">{totals.quantity}</label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-3">
          {totals.unitPrice.toFixed(2)} {currency}
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-3">
          {totals.discount ? `${totals.discount.toFixed(2)} ${currency}` : ""}
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}>
        <label className="label-3">
          {totals.total.toFixed(2)} {currency}
        </label>
      </div>
      <div className={`${isPrint ? "d-block" : "d-none d-lg-block"}`}></div>
      <div className="text-end">
        <label className="label-3">
          {totals.totalWithVat.toFixed(2)} {currency}
        </label>
      </div>
    </div>
  );
});
